import { useRef, useState } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { object } from 'prop-types';
import { Banner } from '@fff-web/cms';
import { useCreditCardFormState } from '@fff-web/fff-payments';
import Layout from '../../components/Layout';
import useGiftCards from '../../hooks/useGiftCards';
import useInputStatus from '../../hooks/useInputStatus';
import { usePageTracking } from '../../hooks/usePageTracking';
import { createGiftCard } from '../../services/gifting';
import normalizers from '../../utils/forms/normalizers';
import validators from '../../utils/forms/validators';
import GiftCardSelector from './GiftCardSelector';
import Header from './Header';
import { handleCreateGiftCardResponse } from './helpers';
import HowSendItSelector from './HowSendItSelector';
import ErrorModal from './Modals/ErrorModal';
import Recipient from './Recipient';
import RecurlyGiftCardPayForm from './RecurlyGiftCardPayForm';
import Section from './Section';
import Sender from './Sender';
import paymentIcons from './assets/payment-icons.svg';
import * as styles from './gift.module.scss';

export const PICK_GIFT_AMOUNT_SECTION_ID = 'pick-gift-amount';

const GiftCardHomePage = ({ history }) => {
  const [lastState, setLastRequestState] = useState({
    errorPaymentMessage: '',
    showRetry: false,
    isLoading: false,
  });
  const { giftCards, selectedGiftCard, selectGiftCard } = useGiftCards();
  const [notDeliveryByMail, setNotDeliveryByMail] = useState(false);
  const [waitingCaptcha, setWaitingCaptcha] = useState({ isWaiting: false, recurlyToken: '' });

  const captchaRef = useRef(null);
  const requiredEmailValidators = [
    validators.emailFormat,
    (value) => validators.requiredText(value, 'email'),
  ];
  const recipientName = useInputStatus(
    [(value) => validators.requiredText(value, 'first name')],
    normalizers.names,
  );
  const recipientLastName = useInputStatus(
    [(value) => validators.requiredText(value, 'last name')],
    normalizers.names,
  );
  const recipientEmailValidators = notDeliveryByMail
    ? [validators.emailFormat]
    : requiredEmailValidators;
  const recipientEmail = useInputStatus(recipientEmailValidators, normalizers.email);
  const message = useInputStatus([], null);

  const senderName = useInputStatus(
    [(value) => validators.requiredText(value, 'name')],
    normalizers.names,
  );
  const senderLastName = useInputStatus(
    [(value) => validators.requiredText(value, 'last name')],
    normalizers.names,
  );
  const senderEmail = useInputStatus(requiredEmailValidators, normalizers.email);
  const payFieldsState = useCreditCardFormState(true);

  const notHasError =
    recipientName.isTouched &&
    senderName.isTouched &&
    senderEmail.isTouched &&
    senderLastName.isTouched &&
    !recipientName.hasError &&
    !senderName.hasError &&
    !senderEmail.hasError &&
    !senderLastName.hasError &&
    (notDeliveryByMail ||
      (!notDeliveryByMail && recipientEmail.isTouched && !recipientEmail.hasError)) &&
    (notDeliveryByMail ||
      (!notDeliveryByMail && recipientLastName.isTouched && !recipientLastName.hasError));

  const paymentInformation = (recurlyToken) => ({
    giftAmount: selectedGiftCard.value * 100,
    token: recurlyToken.id,
  });
  const handleCallbackHCaptcha = (hCaptchaToken) => {
    const paymentInformationR = paymentInformation(waitingCaptcha.recurlyToken);
    const recipientR = recipient();
    const senderR = sender();

    createGiftCard(paymentInformationR, recipientR, senderR, hCaptchaToken)
      .then((response) => {
        handleCreateGiftCardResponse(
          response,
          setLastRequestState,
          history,
          senderR,
          recipientR,
          paymentInformationR,
        );
      })
      .catch((error) => {
        console.error(error);
        setWaitingCaptcha({ isWaiting: false, recurlyToken: '' });
      });
  };

  const handleOnExpireHCaptcha = () => {
    console.log('hCaptcha Token Expired');
  };

  const handleOnErrorHCaptcha = (err) => {
    console.log(`hCaptcha Error: ${err}`);
  };

  const recipient = () => ({
    firstName: recipientName.value,
    lastName: recipientLastName.value,
    email: recipientEmail.value,
    message: message.value,
    isASuprise: notDeliveryByMail,
  });
  const sender = () => ({
    email: senderEmail.value,
    firstName: senderName.value,
    lastName: senderLastName.value,
  });

  const handleOnCloseErrorModal = () => {
    setLastRequestState({
      errorPaymentMessage: '',
      showRetry: false,
      isLoading: false,
    });
  };

  const handleSubmit = (recurlyToken) => {
    if (!notHasError) {
      console.warn('this should not happen, the forms have errors');
      return;
    }

    captchaRef.current.execute();
    setWaitingCaptcha({ isWaiting: true, recurlyToken });
  };

  const handleNotDeliveryByEmail = (notDeliveryByMailSelected) => {
    if (notDeliveryByMailSelected) {
      recipientEmail.reset();
    }
    setNotDeliveryByMail(notDeliveryByMailSelected);
  };

  usePageTracking();

  return (
    <Layout pageTitle="FabFitFun E-Gift Card">
      <Banner name="gifting" className={styles.banner} />
      <div className={styles.container}>
        <Header />
        <div className={styles.subContainer}>
          <Section
            id={PICK_GIFT_AMOUNT_SECTION_ID}
            title="Pick your gift amount"
            hasBottomBorder
            className={styles.giftAmount}
          >
            <GiftCardSelector
              name="couponAmount"
              onCheck={(id, value) => {
                selectGiftCard({ id, value });
              }}
              giftCards={giftCards}
              selected={selectedGiftCard.id}
            />
          </Section>
          <Section title="How do you want to send it?" className={styles.sendIt}>
            <HowSendItSelector
              notDeliveryByEmail={notDeliveryByMail}
              onSelect={handleNotDeliveryByEmail}
            />
          </Section>
          <Section title="Who are you gifting this to?">
            <Recipient
              deliveryByEmail={!notDeliveryByMail}
              name={recipientName}
              email={recipientEmail}
              message={message}
              lastName={recipientLastName}
            />
          </Section>
          <Section title="Who’s it from?" className={styles.sender}>
            <Sender name={senderName} email={senderEmail} lastName={senderLastName} />
          </Section>
          <Section
            title="Payment"
            image={paymentIcons}
            hasBottomBorder={false}
            className={styles.payment}
          >
            <RecurlyGiftCardPayForm
              isDisabled={!notHasError}
              onSubmit={handleSubmit}
              payFieldsState={payFieldsState}
            />
          </Section>
          <div className="hcaptcha">
            <HCaptcha
              sitekey={process.env.REACT_APP_HCAPTCHA_SITEKEY}
              size="invisible"
              onVerify={handleCallbackHCaptcha}
              onError={handleOnErrorHCaptcha}
              onExpire={handleOnExpireHCaptcha}
              ref={captchaRef}
              id="hcaptchaWidget"
            />
          </div>
        </div>
      </div>
      <ErrorModal
        isOpen={lastState.errorPaymentMessage !== '' || lastState.showRetry}
        onClose={handleOnCloseErrorModal}
        isTransactionDeclined={lastState.errorPaymentMessage !== ''}
        transactionDeclinedMsg={lastState.errorPaymentMessage}
      />
    </Layout>
  );
};

GiftCardHomePage.propTypes = {
  history: object,
};

export default GiftCardHomePage;
